/** @module SiteAdmin/CopyForm */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/data/ItemFileWriteStore",
	"csi",
	"csi/_base/underscore",
	"csi/def",
	"csi/def/Field",
	"csi/def/Relationship",
	"csi/form/Widget",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/Writer"
], function(array, declare, lang, ItemFileWriteStore, csi, _, def, Field, Relationship, Widget, html, locale, reflection, xml, Query, Writer) {

	return declare(Widget, {
		FIELD_TEMPLATE: "CopyTemplates",

		// Boolean: tracking state of build process
		_bBuildingFields: false,

		// Node: node specifying copy options
		_copyOptionsNode: null,

		// Node: destination structure item
		destStructureItem: null,

		/**
		 * Get the url of copy context
		 * @return {string}
		 */
		getCopyContextURL: function() {
			return this.defView.getSource().getParent().$URL;
		},

		getFieldNode: function(/*Field*/field, /*Boolean*/bTargetOnly, /*Boolean*/bCurrent) {
			// summary: return options node if field attribute exists on options node
			if ((field.m_id == field._idOption) && xml.nodeHasAttribute(this._copyOptionsNode, field._idOption)) {
				return this._copyOptionsNode;
			}

			return this.inherited(arguments);
		},

		getFieldStore: function(/*Field*/field, /*String*/value) {
			// summary: return store for boolean maps if it exists
			return field._storeBooleanMap || this.inherited(arguments);
		},

		getValueForField: function(/*Field*/field, /*Node?*/node) {
			// summary: if value exists on copyOptionNode, then use it
			if (field._idOption) {
				var v = this._copyOptionsNode[field._idOption];
				if (v != null) {
					// FIX: don't like doing this, but needed to fix Theme on Style Copy form;
					// why doesn't FilteringSelect do this automatically when creating widget?
					if (xml.isValidURL(v)) {
						this.cacheView.getNode(v, true);
					}

					return v;
				}
			}

			return this.inherited(arguments);
		},

		isFieldEditable: function(/*Field*/field) {
			// summary: over-writes option field 'edit' state
			if (this._copyOptionsNode) {
				var fieldLocked = this._copyOptionsNode[field._idOption + "Locked"];
				if (fieldLocked != null) {
					return !fieldLocked;
				}
			}
			// Default processing
			return this.inherited(arguments);
		},

		onChangeTemplate: function(/*def.View*/formView, /*Field*/field, /*Any*/value) {
			// summary: re-fetches copy options and re-draw optional fields
			this._deleteCopyOptionsFields();

			if (value == csi.EMPTY_URL) {
				this._copyOptionsNode = null;
			} else {
				this._getCopyOptions(value);
			}

			if (this._bBuildingFields) {
				// if still building initial form fields, wait to build copy option fields
				setTimeout(lang.hitch(this, this._buildCopyOptionsFields), 1);
			} else {
				// otherwise build new fields now
				this._buildCopyOptionsFields();
			}
		},

		onSetDefaultValue: function(/*Field*/field, /*Store*/store, /*Any*/value) {
			// summary: if there are no templates, then hide field and load default copy options; otherwise set template if there is one
			if ((value == csi.EMPTY_URL) && (field.getControlName() == this.FIELD_TEMPLATE)) {
				if (store.isStoreEmpty()) {
					this.setFieldHidden(field, true);
					this.onChangeTemplate(this.defView, field, null);
				}
			} else {
				this.inherited(arguments);
			}
		},

		/**
		 * @override
		 * Reset form data when form is submitted, in case there was an error
		 */
		validateForm: function() {
			this.defView.m_formData.setFields(null);
			return this.inherited(arguments);
		},

		_appendCopyOptionsFields: function() {
			// summary: store options node and continue with building view
			var defView = this.defView;
			var fields = defView.m_fields;
			var copyOptionsNode = this._copyOptionsNode;

			// Copy options node class and localization map
			var rc = reflection.getNodeClass(copyOptionsNode);
			var lmap = defView.getLocaleMap(rc);

			// Copied type
			var copiedNodeRC = reflection.getClass(copyOptionsNode.RootType);
			var copiedNodeLMap = defView.getLocaleMap(copiedNodeRC);

			var optionFields = array.map(copyOptionsNode["Index"], function(idOption) {
				// If field already configured in form, then skip
				var optionType = copyOptionsNode[idOption + "OptionType"];
				var id = idOption.substring(1);
				var field = defView.getField(id);
				if (!field) {
					field = lang.mixin(new Field(), {_idOption: idOption});

					if (optionType == "VOPT") {
						field.m_id = id;

						if (field.m_id == "PreventDuplicateCarryover") {
							field.m_type = "boolean";
							field.m_typeControl = "Checkbox";
						}

						field.resolve(copiedNodeLMap, copiedNodeRC, copyOptionsNode);
						field.m_bRequired = copyOptionsNode[idOption + "Locked"] ? false : copyOptionsNode[idOption + "Required"];
						field.omitFromReport = !field.m_rca;

						if (field.heading) {
							field.heading.resolveHeading(locale.localeResolver, field.m_rca);
						}
					} else if (optionType == "OPT") {
						var type = xml.nodeAttributeGetType(copyOptionsNode, idOption);
						field.m_id = idOption;
						field.m_type = type;
						field.omitFromReport = true;
						field.resolve(lmap, rc, copyOptionsNode);

						// BooleanMap requires specialized handling
						if (type == xml.ATTR_BOOLEANMAP) {
							this._processBooleanMapField(field, copyOptionsNode);
						}
					} else {
						// Copy Options Node fields (CVOPT, etc.)
						field.m_id = idOption;
						field.omitFromReport = true;
						field.resolve(lmap, rc, copyOptionsNode);
					}

					// Add field to view
					fields.push(field);
				}

				// Prefix parameter with OptionType and do not use id's underscore prefix
				field.idParameter = optionType ? (optionType + "." + id) : id;

				// Visible (Requires additional logic on the client side)
				// field.bHidden = !copyOptionsNode[idOption + "Visible"];

				return field;
			}, this);

			this._buildFields(optionFields);
		},

		_buildFields: function(/*Field[]*/fields) {
			// summary: track when building fields
			this._bBuildingFields = true;
			this.inherited(arguments);
			this._bBuildingFields = false;
		},

		_buildCopyOptionsFields: function() {
			// summary: update fields based on copy options and render
			if (this._copyOptionsNode) {
				this._deleteCopyOptionsFields();
				this._appendCopyOptionsFields();
			}

			this.publishDomChange();
		},

		_processBooleanMapField: function(/*Field*/field, /*Node*/copyOptionsNode) {
			var templateNode = this.cacheView.getNode(this.defView.getFieldValue(this.FIELD_TEMPLATE), true);
			var localePrefix = "CopyOption-";
			var children = [];
			var firstMapOptionName;
			var idOption = field._idOption;
			var editableMap = this.isFieldEditable(field);
			var groupDisabled = true;
			var groupValue = false;
			var store = null;

			function getOptionLocaleGroupName(optGroupName, optName) {
				var localOptGroupName;
				var localValue = locale.resource(localePrefix + optGroupName + "." + optName);

				if (localValue.indexOf(localePrefix, 0) < 0) {
					var b = localValue.split(".");
					localOptGroupName = (b ? b[0] : optGroupName);
				} else {
					// There is no localized value
					localValue = locale.resource(localePrefix + optGroupName);
					if (localValue.indexOf(localePrefix, 0) < 0) {
						localOptGroupName = localValue;
					}
				}
				// Be sure it's not empty
				if (localOptGroupName == null || localOptGroupName.length == 0) {
					localOptGroupName = optGroupName;
				}

				return localOptGroupName;
			};

			function getOptionLocaleName(optGroupName, optName) {
				if (optGroupName == "CopiedStyleSampleTypes") {
					return locale.getEnumName(optName);
				}

				var localOptName;
				var localValue = locale.resource(localePrefix + optGroupName + "." + optName);

				if (localValue.indexOf(localePrefix, 0) < 0) {
					var b = localValue.split(".");
					localOptName = (b.length > 1 ? b[1] : optName);
				} else {
					// There is no localized value
					localValue = locale.resource(localePrefix + optGroupName);
					if (localValue.indexOf(localePrefix, 0) < 0) {
						// Must be a type name (for options with filtered types)
						localOptName = locale.type(optName);
					}
				}
				// Be sure localized option name is not empty
				if (localOptName == null || localOptName.length == 0) {
					localOptName = optName;
				}

				return localOptName;
			};

			// create tree store
			csi.applyForEach(copyOptionsNode[idOption], function(value, key) {
				var disabled = !value;

				if (value && templateNode && templateNode[idOption]) {
					value = templateNode[idOption][key];
				}

				firstMapOptionName = firstMapOptionName || key;
				groupValue = groupValue || value;
				groupDisabled = groupDisabled && disabled;

				children.push({
					id: key,
					name: getOptionLocaleName(idOption.substring(1), key),
					type: "leaf",
					iconClass: "checkboxTreeIcon",
					value: value,
					checkbox: {_type: "object", disabled: !editableMap || disabled}		// define _type so that object is not interpreted as store item.
				});
			});

			if (children.length) {
				store = new ItemFileWriteStore({
					data: {
						identifier: "id",
						label: "name",
						items: [{
							id: idOption,
							name: locale.resource("SelectAll"),
							type: "group",
							children: csi.sort(children, "name"),
							iconClass: "checkboxTreeIcon",
							labelClass: "treeNodeLabelGroup",
							value: groupValue,
							checkbox: {_type: "object", disabled: (!editableMap || groupDisabled)}
						}]
					}
				});
			}

			// Set field properties
			field.m_label = getOptionLocaleGroupName(idOption.substring(1), firstMapOptionName);
			field._storeBooleanMap = store;
		},

		_deleteFormField: function(/*String*/id, /*Boolean?*/bKeepDom) {
			var defView = this.defView;
			var fields = defView.m_fields;
			var field = defView.getField(id);

			if (field != null) {
				// Destroy the field row
				var fieldRow = this.getFieldRow(field);
				if (fieldRow && !bKeepDom) {
					html.destroyElement(fieldRow);
				}

				// Remove the field
				fields.splice(array.indexOf(fields, field), 1);
			}
		},

		_deleteCopyOptionsFields: function(/*Boolean?*/bKeepDom) {
			if (this._copyOptionsNode) {
				array.forEach(this._copyOptionsNode["Index"], function(idOption) {
					this._deleteFormField(idOption, bKeepDom);				// '_xxx' field
					this._deleteFormField(idOption.substring(1), bKeepDom);	// 'xxx' field
				}, this);
			}
		},

		_getCallMethod: function(/*Writer*/w) {
			return null;
		},

		_getCopyOptions: function(/*String?*/urlTemplate) {
			// summary: get copy options for template
			var urlOptions = (urlTemplate || csi.EMPTY_URL) + "/CopyOptions";

			this._copyOptionsNode = this.cacheView.getNode(urlOptions);

			// Fetch it from server
			var w = new Writer();
			var call = this._getCallMethod(w);

			call.addParameter("URL", xml.ATTR_REF, this.getViewURL());
			call.addParameter("CopyOptionsURL", xml.ATTR_REF, urlOptions);
			call.addParameter("ContextURL", xml.ATTR_REF, this.getCopyContextURL());

			if (urlTemplate) {
				call.addParameter("CopyOptionsTemplateURL", xml.ATTR_REF, urlTemplate);
			}

			w.setReport();

			var response = w.execute({sync: true});
			if (response && response.isSuccessful()) {
				// Cache response
				this.cacheView.cacheResponse(response);
				this._copyOptionsNode = this.cacheView.getNode(urlOptions);

				// Overwrite report node flags so that fields are seen as editable
				this._copyOptionsNode.$FG = 0;
			}
		},

		_getTemplateQuery: function() {
			// summary: setup query for copy options
			return new Query().Attr("RootType", xml.OP_EQUALS, this.getViewNode().$Type);
		},

		_getTemplateType: function() {
			// summary: return type of copy options
			return "CopyOptions";
		},

		_redraw: function() {
			// summary: reset copy option fields before building view again
			this._deleteCopyOptionsFields(true);
			this._copyOptionsNode = null;
			this.inherited(arguments);
		},

		_resolveView: function() {
			// summary: insert field for copy option templates
			this.insertField(lang.mixin(new Field(), {
				m_label: this.FIELD_TEMPLATE,
				m_onChange: "onChangeTemplate",
				m_typeControl: def.FIELD_FILTERING_SELECT,
				m_bRequired: true,
				m_relationship: lang.mixin(new Relationship(), {
					m_url: reflection.URL_SITE,		// use this so store will be cached globally
					m_query: this._getTemplateQuery(),
					m_typeNode: this._getTemplateType()
				})
			}), 0);

			this.inherited(arguments);
		}
	});
});